import React, { useCallback, useEffect, useState } from 'react';
import ModalTransition from '@atlaskit/modal-dialog/modal-transition';
import { fg } from '@atlassian/jira-feature-gating';
import type { RouteContext } from '@atlassian/react-resource-router';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import WorkflowViewDiagramModal from '@jira/project-settings__workflow-view-diagram-modal/src/WorkflowViewDiagramModalApp.tsx';
import { LegacyPage } from '@jira/router-routes__common/src/ui/legacy-page/main.tsx';
import AddExistingWorkflowPreview from './AddExistingWorkflowPreview.tsx';

type Props = RouteContext;

const LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS = 'project-config-workflow-designer-link';
const LEGACY_WORKFLOWS_PANEL_BODY_CLASS = 'project-config-workflow-panel-body';

// This id is the main entry point for the project settings workflows page that can be found in:
// `jira-components/jira-plugins/jira-project-config-plugin/plugin/src/main/resources/jira-project-config-plugin/workflows/templates/workflows-tab.vm#lines-23`
const LEGACY_PROJECT_CONFIG_PANEL_WORKFLOWS_ID = 'project-config-panel-workflows';

const getLegacyWorkflowDiagramLinks = () => {
	const workflowDiagramLinksElem = globalThis.document?.getElementsByClassName(
		`${LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS}`,
	);

	return [...(workflowDiagramLinksElem ?? [])];
};

const getLegacyWorkflowsPanelBody = () => {
	const workflowsPanel = globalThis.document?.getElementsByClassName(
		`${LEGACY_WORKFLOWS_PANEL_BODY_CLASS}`,
	);

	const workflowsPanelArray = [...(workflowsPanel ?? [])];
	return workflowsPanelArray.length > 0 ? workflowsPanelArray[0] : undefined;
};

const getLegacyProjectConfigPanelWorkflows = () => {
	return globalThis.document?.getElementById(LEGACY_PROJECT_CONFIG_PANEL_WORKFLOWS_ID);
};

export const ProjectSettingsWorkflows = (props: Props): React.JSX.Element => {
	const [selectedWorkflow, setSelectedWorkflow] = useState<string | undefined>(undefined);

	const handleOnClose = useCallback(() => {
		setSelectedWorkflow(undefined);
	}, [setSelectedWorkflow]);

	const getProjectId = () => {
		const elm = globalThis.document?.getElementsByClassName('workflow-an-info-container')[0];
		const projectId = elm?.getAttribute('data-project-id');
		return projectId !== null ? projectId : undefined;
	};

	useEffect(() => {
		if (!fg('nike_entrypoint_migration_project_workflow')) {
			let observer: MutationObserver | null = null;

			const handleClicksOnWorkflowDiagramLinks = () => {
				const workflowsTable = getLegacyWorkflowsPanelBody();
				if (!workflowsTable) {
					return;
				}

				// Register click handlers to all legacy workflow diagram links
				workflowsTable.addEventListener('click', (event: Event) => {
					// We only want to handle clicks on the legacy workflow diagram links.
					// by checking the closest ancestor element with the class 'project-config-workflow-designer-link'
					// since the `event.target`can be any element inside the link (span for example)
					// oxlint-disable-next-line typescript/consistent-type-assertions
					const diagramLinkElement = (event.target as Element).closest(
						`.${LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS}`,
					);

					if (diagramLinkElement) {
						event.preventDefault();
						// We are missing the data attribute with the workflow name in the link element,
						// so we need to find the closest ancestor row and get the workflow name from there
						const workflowName = diagramLinkElement?.getAttribute('data-workflowname');
						if (workflowName) {
							setSelectedWorkflow(workflowName);
						}
					}
				});
			};

			// If there are already legacy workflow diagram links, we register the click handler immediately.
			if (getLegacyWorkflowDiagramLinks().length > 0) {
				handleClicksOnWorkflowDiagramLinks();
			} else {
				// We only execute this once, when the LEGACY_PROJECT_CONFIG_PANEL_WORKFLOWS_ID is populated
				// This is because the project settings workflows page is rendered server-side and the links are
				// dynamically added to the DOM after the initial render.
				observer = new MutationObserver(() => {
					handleClicksOnWorkflowDiagramLinks();
					observer?.disconnect();
				});
				const projectConfigPanelWorkflows = getLegacyProjectConfigPanelWorkflows();
				if (projectConfigPanelWorkflows) {
					observer.observe(projectConfigPanelWorkflows, {
						childList: true,
						subtree: true,
					});
				}
			}

			return () => {
				getLegacyWorkflowsPanelBody()?.removeEventListener(
					'click',
					handleClicksOnWorkflowDiagramLinks,
				);
				observer?.disconnect();
			};
		}

		let observer: MutationObserver | null = null;
		let registeredWorkflowsTable: Element | undefined;

		const handleWorkflowDiagramLinkClick = (event: Event) => {
			// We only want to handle clicks on the legacy workflow diagram links.
			// by checking the closest ancestor element with the class 'project-config-workflow-designer-link'
			// since the `event.target`can be any element inside the link (span for example)
			// oxlint-disable-next-line typescript/consistent-type-assertions
			const diagramLinkElement = (event.target as Element).closest(
				`.${LEGACY_WORKFLOW_DIAGRAM_LINKS_CLASS}`,
			);

			if (diagramLinkElement) {
				event.preventDefault();
				// We are missing the data attribute with the workflow name in the link element,
				// so we need to find the closest ancestor row and get the workflow name from there
				const workflowName = diagramLinkElement.getAttribute('data-workflowname');
				if (workflowName) {
					setSelectedWorkflow(workflowName);
				}
			}
		};

		const registerWorkflowDiagramLinkClickHandler = () => {
			const workflowsTable = getLegacyWorkflowsPanelBody();
			if (!workflowsTable) {
				return false;
			}

			workflowsTable.addEventListener('click', handleWorkflowDiagramLinkClick);
			registeredWorkflowsTable = workflowsTable;
			return true;
		};

		if (!registerWorkflowDiagramLinkClickHandler()) {
			// The project settings workflows page is rendered server-side and the panel and links
			// are dynamically added to the DOM after the initial render. Keep observing until the
			// workflows table exists so an early mutation cannot permanently miss registration.
			observer = new MutationObserver(() => {
				if (registerWorkflowDiagramLinkClickHandler()) {
					observer?.disconnect();
				}
			});
			const observationTarget = getLegacyProjectConfigPanelWorkflows() ?? globalThis.document?.body;
			if (observationTarget) {
				observer.observe(observationTarget, {
					childList: true,
					subtree: true,
				});
			}
		}

		return () => {
			registeredWorkflowsTable?.removeEventListener('click', handleWorkflowDiagramLinkClick);
			observer?.disconnect();
		};
	}, []);

	return (
		<JSErrorBoundary
			id="read-only-workflow-diagram-error-boundary"
			packageName="jiraRouterRoutesLegacyProjectSettingsWorkflowsEntries"
			teamName="helix"
			fallback="flag"
		>
			<LegacyPage {...props} />
			<AddExistingWorkflowPreview />
			<ModalTransition>
				{selectedWorkflow && (
					<WorkflowViewDiagramModal
						projectId={getProjectId()}
						workflowIdentifier={{ workflowName: selectedWorkflow }}
						onClose={handleOnClose}
					/>
				)}
			</ModalTransition>
		</JSErrorBoundary>
	);
};
